import { useState } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert, ClipboardCheck, Users, Hammer, ArrowRight, TrendingUp, MessageSquareText, Sparkles, Recycle, BadgeCheck, Camera } from "lucide-react";
import HubCmdLogo from "@/components/HubCmdLogo";
import ContactRequestDialog from "@/components/ContactRequestDialog";

const FEATURES = [
  { icon: Hammer, title: "On-the-fly Work Requests", desc: "Spot a problem on the floor? Snap a photo, tag the asset, fire off a maintenance work request in seconds — supervisors get the request with photo evidence attached and a traceable WO-NNNN ID.", color: "#1a1a1a" },
  { icon: ClipboardCheck, title: "Audits & 5S Walks", desc: "Build standard work templates once. Run daily floor audits and Gemba walks with pass/fail, notes, and photo proof on every step.", color: "#ff4500" },
  { icon: TrendingUp, title: "Trends · ISO 9001 + GFSI", desc: "Auto-generated PDFs aligned to ISO 9001 §9.3 and GFSI §1.1.2 Management Review. Open them in-app as compliance meetings with signed attendance.", color: "#1a1a1a" },
  { icon: Recycle, title: "Lean Continuous Improvement", desc: "Tag every action against the 8 wastes. Track PDCA cycles. Surface aging corrective actions before re-inspection bites.", color: "#ff4500" },
  { icon: Users, title: "Hub Channels", desc: "Create or join a hub, invite by email, then chat, share, and coordinate without leaving the console.", color: "#1a1a1a" },
  { icon: BadgeCheck, title: "Compliance Audit Trail", desc: "Every action change is timestamped and signed. Meeting completions generate a tamper-evident signed PDF for ISO + GFSI auditors.", color: "#ff4500" },
];

export default function Landing() {
  const [contactOpen, setContactOpen] = useState(false);
  return (
    <div className="min-h-screen bg-[#f7f5f2] text-[#1a1a1a]">
      {/* Top bar */}
      <header className="absolute top-0 inset-x-0 z-20">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <HubCmdLogo size={36} variant="shield" inverted />
            <span className="font-display font-black text-lg tracking-tight"><span>hub</span><span className="text-[#ff5f15]">CMD</span></span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="login-link" className="text-white/85 text-xs uppercase tracking-wider font-bold hover:text-white">
              Sign in
            </Link>
            <button data-testid="get-started-btn" onClick={() => setContactOpen(true)} className="btn-pill bg-white text-[#1a1a1a] hover:bg-[#ff4500] hover:text-white">
              Get started <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </header>

      {/* Hero — gradient strip with content card overlap */}
      <section className="relative hero-navy text-white pt-28 pb-44 overflow-hidden">
        <div className="absolute inset-0 dotted opacity-30" />
        <div className="absolute -top-32 right-10 w-96 h-96 rounded-full bg-[#ff4500]/30 blur-3xl" />
        <div className="absolute bottom-20 -left-20 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-3.5 py-1.5 backdrop-blur-sm">
              <Sparkles className="w-3.5 h-3.5 text-[#ff5f15]" />
              <span className="font-mono text-[11px] uppercase tracking-[0.2em]">ISO 9001 · GFSI · Lean Manufacturing</span>
            </div>
            <h1 className="mt-6 font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
              Good morning,<br/>
              <span className="text-[#ff5f15]">command your floor.</span>
            </h1>
            <p className="mt-6 text-base text-white/80 leading-relaxed max-w-xl">
              The operations hub built around <b className="text-white">Lean Manufacturing principles</b>, <b className="text-white">ISO 9001 §9.3</b> and <b className="text-white">GFSI Management Review</b>. Tasks, audits, photo-evidenced maintenance work requests, hub chat, 8-Wastes tagging, PDCA cycle tracking, auto-generated compliance reports, and signed-attendance meetings — in one console for managers who actually run the floor.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button onClick={() => setContactOpen(true)} data-testid="hero-cta-primary" className="btn-pill btn-pill-primary">
                Request access <ArrowRight className="w-3.5 h-3.5" />
              </button>
              <Link to="/login" data-testid="hero-cta-secondary" className="btn-pill btn-pill-ghost">
                Sign in
              </Link>
            </div>
          </div>
          <div className="lg:col-span-6 hidden lg:block">
            <div className="relative">
              {/* Commercial / demo video — iter54: enlarged, decorative overlays removed so controls stay clickable */}
              <div className="relative surface-card-lift rounded-3xl overflow-hidden bg-black shadow-[0_30px_80px_-20px_rgba(255,69,0,0.35)]" data-testid="hero-commercial-video-wrap">
                <div className="absolute top-3 left-3 z-10 inline-flex items-center gap-1.5 bg-[#ff4500] text-white px-2.5 py-1 rounded-full font-mono text-[9px] uppercase tracking-[0.2em] font-bold pointer-events-none">
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Live demo · 90 sec
                </div>
                <video
                  data-testid="hero-commercial-video"
                  className="w-full h-auto aspect-video object-cover"
                  src="/video/hubcmd-commercial.mp4"
                  autoPlay
                  muted
                  loop
                  playsInline
                  controls
                  preload="metadata"
                />
              </div>
              {/* iter54 — decorative floating cards removed: they were overlapping the video controls */}
            </div>
          </div>
          {/* Mobile: video shows below CTA */}
          <div className="lg:hidden col-span-1 mt-4">
            <div className="relative surface-card-lift rounded-2xl overflow-hidden bg-black">
              <div className="absolute top-2 left-2 z-10 inline-flex items-center gap-1.5 bg-[#ff4500] text-white px-2 py-0.5 rounded-full font-mono text-[9px] uppercase tracking-[0.2em] font-bold">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Demo · 90 sec
              </div>
              <video
                data-testid="hero-commercial-video-mobile"
                className="w-full h-auto aspect-video object-cover"
                src="/video/hubcmd-commercial.mp4"
                autoPlay
                muted
                loop
                playsInline
                controls
                preload="metadata"
              />
            </div>
          </div>
        </div>
        {/* KPI strip overlap */}
        <div className="relative max-w-7xl mx-auto px-6 mt-20">
          <div className="grid grid-cols-3 gap-4">
            {[["6", "Integrated modules"], ["100%", "Photo evidence"], ["weekly→quarterly", "Auto reports"]].map(([n, l]) => (
              <div key={l} className="surface-card rounded-2xl px-5 py-4">
                <div className="font-display font-black text-2xl text-[#1a1a1a]">{n}</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#1a1a1a]/55 mt-1 font-mono">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features grid */}
      <section className="relative max-w-7xl mx-auto px-6 py-24">
        <div className="mb-12 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#ff4500]">// Modules</div>
            <h2 className="font-display font-black text-3xl lg:text-4xl mt-2 max-w-xl leading-tight">
              Every workflow a floor manager runs, in one place.
            </h2>
          </div>
          <button onClick={() => setContactOpen(true)} data-testid="features-cta" className="btn-pill btn-pill-primary">
            Talk to us <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className={`surface-card rounded-2xl p-6 hover:shadow-lift transition-all duration-200 hover:-translate-y-0.5`} data-testid={`feature-${i}`}>
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: f.color }}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <div className="mt-4 font-display font-bold text-base">{f.title}</div>
                <p className="mt-2 text-sm text-[#1a1a1a]/75 leading-relaxed">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA strip */}
      <section className="relative pb-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="hero-scarlet rounded-3xl p-10 lg:p-14 text-white flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 overflow-hidden relative">
            <div className="absolute inset-0 dotted opacity-25" />
            <div className="relative max-w-xl">
              <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/80">// Built for the floor</div>
              <h3 className="mt-2 font-display font-black text-2xl lg:text-3xl leading-tight">
                Trade morning admin for shop-floor time.
              </h3>
              <p className="mt-3 text-sm text-white/85 leading-relaxed">
                Enterprise onboarding with your TruPath Advisor. Templates library, dedicated controller seat, and standards mapped to your program — configured before your team touches it.
              </p>
            </div>
            <button onClick={() => setContactOpen(true)} data-testid="cta-strip-button" className="relative btn-pill bg-white text-[#ff4500] hover:bg-[#1a1a1a] hover:text-white">
              Request access <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </section>

      {/* Compliance frameworks strip */}
      <section className="border-y border-[#1a1a1a]/10 bg-[#f7f5f2]" data-testid="compliance-strip">
        <div className="max-w-7xl mx-auto px-6 py-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#ff4500]">// Compliance frameworks supported</div>
              <h3 className="mt-1 font-display font-black text-2xl lg:text-3xl leading-tight">23 standards. Audit-ready out of the box.</h3>
              <p className="mt-2 text-sm text-[#1a1a1a]/70 max-w-xl">Every feature in hubCMD is mapped to clauses in the standards your auditors actually care about — quality, food safety, medical, aerospace, environmental, energy, info-security, asset management.</p>
            </div>
            <Link to="/compliance" data-testid="compliance-strip-cta" className="btn-pill btn-pill-secondary self-start md:self-auto whitespace-nowrap">
              See the full compliance map <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="mt-6 flex flex-wrap gap-1.5" data-testid="compliance-chips">
            {[
              "ISO 9001:2015","ISO 14001:2015","ISO 45001:2018","ISO 13485:2016","ISO/IEC 17025:2017",
              "ISO 50001:2018","ISO/IEC 27001:2022","ISO 55001:2014","ISO 22301:2019","ISO 37001:2016",
              "ISO 22716:2007","ISO 3834-2:2021","IATF 16949:2016","AS9100D:2016","HACCP",
              "SQF Edition 9","BRCGS Food Issue 9","FSSC 22000 v5.1","NADCAP","SA8000:2014",
              "FSC-STD-40-004","FDA cGMP","FSMA · 21 CFR 117","MDSAP","FDA 21 CFR Part 11",
            ].map((s) => (
              <span key={s} className="text-[10px] font-bold uppercase tracking-wider rounded-full bg-white border border-[#1a1a1a]/15 px-3 py-1.5 text-[#1a1a1a] hover:border-[#ff4500] hover:text-[#ff4500] transition-colors cursor-default">{s}</span>
            ))}
          </div>
        </div>
      </section>

      {/* TruPath Advisory cross-promo */}
      <section className="bg-[#0e0e0e] text-white border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-10 grid grid-cols-1 md:grid-cols-[1fr_auto] items-center gap-4">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#ff5f15]">// Companion service · TruPath Advisory</div>
            <h3 className="font-display font-black text-2xl tracking-tight mt-1">Need an expert to stand it up with you?</h3>
            <p className="text-sm text-white/70 max-w-2xl mt-2">hubCMD is built by <b className="text-white">TruPath Advisory</b> — a consulting firm specialising in Lean Manufacturing, Continuous Improvement, and third-party audit program design &amp; implementation. Get hands-on guidance from the people who built the platform.</p>
          </div>
          <a href="https://trupathadvisory.com" target="_blank" rel="noopener noreferrer"
            data-testid="landing-trupath-cta" className="btn-pill bg-[#ff4500] text-white hover:bg-[#cc3500] shadow-glow font-display font-black whitespace-nowrap self-start md:self-auto">
            Visit TruPathAdvisory.com →
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 bg-[#0e0e0e] text-white/55">
        <div className="max-w-7xl mx-auto px-6 py-8 flex items-center justify-between flex-wrap gap-3">
          <div className="font-mono text-[11px] uppercase tracking-[0.2em]">© hubCMD · Operations Command Hub</div>
          <Link to="/compliance" data-testid="footer-compliance-link" className="font-mono text-[11px] uppercase tracking-[0.2em] hover:text-[#ff5f15]">Compliance map →</Link>
          <a href="https://trupathadvisory.com" target="_blank" rel="noopener noreferrer" className="font-mono text-[11px] uppercase tracking-[0.2em] hover:text-[#ff5f15]" data-testid="footer-trupath">A TruPath Advisory product →</a>
        </div>
      </footer>
      <FeedbackButtonPlaceholder />
      <ContactRequestDialog open={contactOpen} onClose={() => setContactOpen(false)} />
    </div>
  );
}

// Removed the floating "Feedback" button from public homepage — hubcmd.com is enterprise-onboarded.
function FeedbackButtonPlaceholder() { return null; }
